import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AuthShell from "../components/AuthShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const ROLE_LABELS = {
  admin: "Administrateur Siège",
  licencie: "Licencié",
  commercant: "Commerçant partenaire",
};

function roleHome(role) {
  if (role === "admin") return "/admin";
  if (role === "licencie") return "/licencie";
  if (role === "commercant") return "/commercant";
  return "/";
}

export default function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { user, loginWithToken } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setInfo({ valid: false, reason: "Token manquant" });
      return;
    }
    api.get(`/invitations/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((err) => setInfo({ valid: false, reason: formatApiError(err) }))
      .finally(() => setLoading(false));
  }, [token]);

  if (user) return <Navigate to={roleHome(user.role)} replace />;

  if (loading) {
    return (
      <AuthShell title="Vérification du lien..." subtitle="Un instant.">
        <div className="h-2 bg-[#E5E7EB] rounded animate-pulse" />
      </AuthShell>
    );
  }

  if (!info?.valid) {
    return (
      <AuthShell title="Lien d'invitation invalide" subtitle="Ce lien ne peut pas être utilisé.">
        <div data-testid="invitation-invalid" className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] text-[#B91C1C] px-4 py-3">
          {info?.reason || "Lien invalide ou expiré."}
        </div>
        <Button
          onClick={() => navigate("/login")}
          className="w-full mt-6 h-11 bg-[#397DFF] hover:bg-[#2860D8] text-white font-semibold rounded-xl"
          data-testid="invitation-back-login"
        >
          Retour à la connexion
        </Button>
      </AuthShell>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invitations/${token}/accept`, { password });
      loginWithToken(data.token, data.user);
      toast.success("Compte activé. Bienvenue !");
      navigate(roleHome(data.user.role), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Activer mon compte"
      subtitle={`Définissez votre mot de passe pour rejoindre Place des Énergies en tant que ${ROLE_LABELS[info.role] || info.role}.`}
    >
      <div className="rounded-xl bg-[#F5F6F9] border border-[#E5E7EB] px-4 py-3 mb-5" data-testid="invitation-info">
        <div className="text-xs uppercase tracking-wider text-[#939BA0]">Invitation pour</div>
        <div className="mt-1 font-semibold text-[#191D1E]">
          {info.prenom} {info.nom}
        </div>
        <div className="text-sm text-[#687175]">{info.email}</div>
        {info.merchant_name && (
          <div className="mt-2 text-sm" data-testid="invitation-merchant">
            <span className="text-[#687175]">Commerce partenaire : </span>
            <strong className="text-[#191D1E]">{info.merchant_name}</strong>
          </div>
        )}
        {info.zone && info.role === "licencie" && (
          <div className="mt-2 text-sm">
            <span className="text-[#687175]">Zone : </span>
            <strong className="text-[#191D1E]">{info.zone}</strong>
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} className="space-y-5" data-testid="set-password-form">
        <div className="space-y-1.5">
          <Label htmlFor="password">Mot de passe (8 caractères min.)</Label>
          <Input id="password" data-testid="set-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirmer le mot de passe</Label>
          <Input id="confirm" data-testid="set-password-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </div>

        {error && (
          <div data-testid="set-password-error" className="px-3 py-2 rounded-lg bg-[#FEF2F2] text-[#B91C1C] text-sm border border-[#FECACA]">
            {error}
          </div>
        )}

        <Button
          type="submit"
          data-testid="set-password-submit"
          disabled={submitting}
          className="w-full h-11 bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl shadow-[0_4px_14px_rgba(249,200,14,0.32)]"
        >
          {submitting ? "Activation..." : "Activer mon compte"}
        </Button>
      </form>
    </AuthShell>
  );
}
